"use client";

import { useCallback, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { CircleCheck, LifeBuoy, X } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { LinkButton } from "@/components/ui/Button";
import { EMPTY_DRAFT, ReportForm, type ReportDraft } from "./ReportForm";
import { langPath, type Lang } from "@/lib/i18n";

/**
 * "Report a problem", within reach on every page a member sees: an icon in the
 * app top bar (members get the app shell everywhere, the public pages
 * included — app/[lang]/(public)/layout.tsx) that opens the report form in a side panel OVER the page, so the
 * member never leaves what they are reporting and the page travels as
 * `pageUrl`. The panel is the only way in — there is no report page — so the
 * trigger is a button; "My reports" renders it too, with `children` as its
 * label.
 *
 * The panel follows the app shell's drawer (components/layout/AppShellClient.tsx):
 * it closes on a route change, and the form inside is a mount that exists only
 * while it is open — which is why the draft lives here, not in the form. After
 * a send the panel shows the confirmation with a link to the report; closing
 * it starts the next report blank.
 */
export function ReportProblem({
  lang,
  portalName,
  className,
  iconClassName = "size-5",
  children,
}: {
  lang: Lang;
  portalName: string;
  /** The trigger's look — the top bar styles it like its neighbouring icons. */
  className: string;
  iconClassName?: string;
  /** A labelled trigger; the default is the top bar's icon. */
  children?: ReactNode;
}) {
  const t = useDict().issues;
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReportDraft>(EMPTY_DRAFT);
  const [sentId, setSentId] = useState<string | null>(null);

  // A report page says nothing about where the problem is.
  const fromPath = pathname && !pathname.includes("/profil/prijave") ? pathname : null;

  const [previousPath, setPreviousPath] = useState(pathname);
  if (pathname !== previousPath) {
    setPreviousPath(pathname);
    setOpen(false);
    // "View report" navigates away from a sent panel: the next one starts blank.
    if (sentId) {
      setSentId(null);
      setDraft(EMPTY_DRAFT);
    }
  }

  function close() {
    setOpen(false);
    if (sentId) {
      setSentId(null);
      setDraft(EMPTY_DRAFT);
    }
  }

  const onSent = useCallback((id: string) => setSentId(id), []);

  return (
    <>
      <button
        type="button"
        title={children ? undefined : t.reportProblem}
        aria-haspopup="dialog"
        onClick={() => {
          // The query string too: a filtered catalog is part of "where".
          setFrom(fromPath ? fromPath + window.location.search : null);
          setOpen(true);
        }}
        className={className}
      >
        {children ?? (
          <>
            <LifeBuoy className={iconClassName} aria-hidden />
            <span className="sr-only">{t.reportProblem}</span>
          </>
        )}
      </button>

      <Dialog open={open} onClose={close} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/40" />
        <DialogPanel
          className="fixed inset-y-0 right-0 flex w-full flex-col overflow-y-auto overscroll-contain border-l border-border bg-background pb-[env(safe-area-inset-bottom)] sm:w-[30rem]"
          style={{ boxShadow: "var(--shadow-lift)" }}
        >
          <div className="sticky top-0 z-10 flex min-h-14 shrink-0 items-center gap-2 border-b border-border/60 bg-background/95 pl-5 pr-2 pt-[env(safe-area-inset-top)] backdrop-blur-md">
            <LifeBuoy className="size-[18px] shrink-0 text-primary" aria-hidden />
            <DialogTitle className="min-w-0 flex-1 truncate font-display text-[15px] font-extrabold text-foreground">
              {t.reportProblem}
            </DialogTitle>
            <button
              type="button"
              onClick={close}
              aria-label={t.closePanel}
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-foreground hover:bg-foreground/10"
            >
              <X className="size-[22px]" aria-hidden strokeWidth={2} />
            </button>
          </div>

          {sentId ? (
            <div className="flex flex-col items-start gap-4 px-5 py-8">
              <CircleCheck className="size-10 text-primary" aria-hidden />
              <p role="status" className="font-display text-[17px] font-bold text-foreground">
                {t.sent}
              </p>
              <p className="text-sm text-muted-foreground">
                {t.formDesc.replaceAll("{portal}", portalName)}
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <LinkButton
                  href={langPath(lang, `/profil/prijave/${encodeURIComponent(sentId)}`)}
                  variant="primary"
                  size="compact"
                >
                  {t.viewReport}
                </LinkButton>
                <button
                  type="button"
                  data-autofocus
                  onClick={close}
                  className="inline-flex h-10 items-center rounded-pill px-4 text-sm font-bold text-foreground hover:bg-foreground/[0.06]"
                >
                  {t.closePanel}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-5 px-5 py-6">
              <p className="text-sm text-muted-foreground">
                {t.formDesc.replaceAll("{portal}", portalName)}
              </p>
              <ReportForm
                lang={lang}
                from={from}
                onSent={onSent}
                draft={draft}
                onDraft={setDraft}
              />
            </div>
          )}
        </DialogPanel>
      </Dialog>
    </>
  );
}
